import { Component, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { DirectionService } from '../../direction.service';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from 'src/app/auth/auth.service';
import { Router, ActivatedRoute } from '@angular/router';
import { DialogService } from 'src/app/dialog/dialog.service';
import { DataSharingService } from '../../main-section/data-shering-service/data-sharing.service';
import { ClustersService } from '../../main-section/choose-branch/clusters.service';

@Component({
  selector: 'app-q-and-a-list',
  templateUrl: './q-and-a.component.html',
  styleUrls: ['./q-and-a.component.css'],
  host: {
    class: 'fill-screen',
  },
})
export class QAndAComponent implements OnInit, OnDestroy {
  isRTL: boolean = true;
  private directionSubscription: Subscription;
  isDarkMode: boolean = false;
  isLoading = false;

  userId: string;
  userIsAuthenticated = false;
  private authStatusSub: Subscription;
  lang: string = 'he';

  /** מספר השאלה הנפתחת מה־URL (1–8), null = ללא פתיחה */
  expandedQuestionIndex: number | null = null;
  /** כשהראוט מוביל לשאלה מסוימת (/qanda/6) – השאלה תהיה ראשונה ברשימה */
  routeQuestionId: number | null = null;

  /** קלסטרים להוראות הגעה – כמו בעמוד /edit */
  clusters: any[] = [];
  clustersLoadCompleted = false;
  selectedArrivalClusterSerialName: string | null = null;
  /** קלאסטר נבחר לשאלה "איזה סוגי נייר יש" */
  selectedPaperTypesClusterSerialName: string | null = null;
  /** מפתח ה־i-explain הפתוח – printerSerialName_paperTypeKey */
  expandedPaperExplainKey: string | null = null;

  get isMobile(): boolean {
    return typeof window !== 'undefined' && window.innerWidth <= 768;
  }

  qAndAs = [
    {
      name: 'how-to-print',
      type: 'general',
    },
    {
      name: 'how-to-print-express',
      type: 'express',
    },
    {
      name: 'how-to-print-plotter',
      type: 'plotter',
    },
  ];

  /** רשימת התכנות במדריך העריכה (שאלה "איך לערוך קובץ להדפסה?") */
  readonly editGuideSoftwareKeys = [
    'q-and-a.2.software.illustrator',
    'q-and-a.2.software.photoshop',
    'q-and-a.2.software.indesign',
    'q-and-a.2.software.autocad',
    'q-and-a.2.software.canva',
    'q-and-a.2.software.powerpoint',
    'q-and-a.2.software.revit',
    'q-and-a.2.software.word',
  ];

  constructor(
    private authService: AuthService,
    private directionService: DirectionService,
    private router: Router,
    private route: ActivatedRoute,
    private dialogService: DialogService,
    private dataSharingService: DataSharingService,
    private clustersService: ClustersService,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.isLoading = true;
    this.loadClusters();

    const id = this.route.snapshot.paramMap.get('id');
    if (id) {
      const n = parseInt(id, 10);
      this.routeQuestionId = n >= 1 && n <= 8 ? n : null;
      this.expandedQuestionIndex = this.routeQuestionId;
    }

    this.route.paramMap.subscribe((params) => {
      const id = params.get('id');
      if (id) {
        const n = parseInt(id, 10);
        this.routeQuestionId = n >= 1 && n <= 8 ? n : null;
        this.expandedQuestionIndex = this.routeQuestionId;
      } else {
        this.routeQuestionId = null;
        this.expandedQuestionIndex = null;
      }
    });

    this.directionSubscription = this.directionService.direction$.subscribe(
      (direction) => {
        this.isRTL = direction === 'rtl';
      }
    );

    this.directionService.isDarkMode$.subscribe((isDarkMode) => {
      this.isDarkMode = isDarkMode;
    });

    this.directionService.currentLanguage$.subscribe((lang) => {
      this.lang = lang;
    });

    this.userIsAuthenticated = this.authService.getIsAuth();
    this.authStatusSub = this.authService
      .getAuthStatusListener()
      .subscribe((isAuthenticated) => {
        this.userIsAuthenticated = isAuthenticated;
        this.userId = this.authService.getUserId();
      });

    this.isLoading = false;
  }

  ngOnDestroy() {
    this.authStatusSub.unsubscribe();
    this.directionSubscription.unsubscribe();
  }

  goToNewOrder() {
    const printingService = localStorage.getItem('printingService');
    const branch = localStorage.getItem('branch');
    if (
      printingService &&
      printingService !== 'null' &&
      printingService !== '' &&
      branch &&
      branch !== 'null' &&
      branch !== ''
    ) {
      this.dialogService.onOpenRightPlaceDialog();
    } else if (branch && branch !== 'null' && branch !== '') {
      this.router.navigate(['/branch']);
    } else {
      this.router.navigate(['/']);
    }
  }

  openWhatsAppEditMode(msg: string) {
    const phoneNumber = '97233746962';
    const message = encodeURIComponent(msg);
    const url = `https://wa.me/${phoneNumber}?text=${message}`;
    window.open(url, '_blank');
  }

  openWhatsAppDefault() {
    this.openWhatsAppEditMode('Hi Eazix, I need some help printing :)');
  }

  goToEditInstructions() {
    this.router.navigate(['/edit']);
  }

  private loadClusters() {
    this.clustersService.getClustersForEditInstructions().subscribe({
      next: (clusters) => {
        this.clusters = clusters ?? [];
        this.clustersLoadCompleted = true;
      },
      error: (err) => {
        console.error('Failed to load clusters for Q&A', err);
        this.clusters = [];
        this.clustersLoadCompleted = true;
      },
    });
  }

  getClusterDisplayName(serialName: string): string {
    if (!serialName) return '';
    const key = 'choose-branch.naming.' + serialName;
    const t = this.translate.instant(key);
    return t !== key ? t : serialName;
  }

  /** שם מדפסת להצגה – plotter/express משתמשים ב־naming.plotter.X / naming.express.X */
  getPrinterDisplayName(serialName: string, type: 'plotter' | 'express'): string {
    if (!serialName) return '';
    const base = serialName.startsWith('plotter.') ? serialName.replace('plotter.', '') : serialName.startsWith('express.') ? serialName.replace('express.', '') : serialName;
    const key = `choose-branch.naming.${type}.${base}`;
    const t = this.translate.instant(key);
    if (t !== key) return t;
    return this.getClusterDisplayName(serialName) || serialName;
  }

  /** כל המדפסות בקלאסטר – פלוטר + אקספרס */
  getPrintersInCluster(cluster: any): { serialName: string; displayName: string; addressKey: string; printType: 'plotter' | 'express' | 'pics' }[] {
    if (!cluster) return [];
    const result: { serialName: string; displayName: string; addressKey: string; printType: 'plotter' | 'express' | 'pics' }[] = [];
    const add = (branch: any, type: 'plotter' | 'express') => {
      if (!branch?.serial_name) return;
      const sn = branch.serial_name;
      const addressKey = sn.startsWith('plotter.') ? sn.replace('plotter.', '') : sn.startsWith('express.') ? sn.replace('express.', '') : sn;
      const printType: 'plotter' | 'express' | 'pics' = type === 'plotter' ? 'plotter' : branch?.isPicsPrinter ? 'pics' : 'express';
      result.push({
        serialName: sn,
        displayName: this.getPrinterDisplayName(sn, type) || sn,
        addressKey,
        printType,
      });
    };
    (cluster.plotter || []).forEach((b: any) => add(b, 'plotter'));
    (cluster.express || []).forEach((b: any) => add(b, 'express'));
    return result;
  }

  /** נתיב לאייקון סוג ההדפסה */
  getPrintTypeIcon(printType: 'plotter' | 'express' | 'pics'): string {
    const icons = { plotter: 'plotter-icon.svg', express: 'express-icon.svg', pics: 'pics-icon.svg' };
    return 'assets/images/' + (icons[printType] || icons.express);
  }

  getShortAddress(addressKey: string, serialName?: string): string {
    let key = 'choose-branch.short-address.' + addressKey;
    let t = this.translate.instant(key);
    if (t === key && serialName) {
      key = 'choose-branch.short-address.' + serialName;
      t = this.translate.instant(key);
    }
    return t !== key ? t : '';
  }

  getArrivalInstructions(addressKey: string, serialName?: string): string {
    let key = 'choose-branch.address-exact.' + addressKey;
    let t = this.translate.instant(key);
    if (t === key && serialName) {
      key = 'choose-branch.address-exact.' + serialName;
      t = this.translate.instant(key);
    }
    return t !== key ? t : '';
  }

  /** סדר תצוגה – כשהראוט מוביל לשאלה (/qanda/6) היא תהיה ראשונה */
  get questionOrder(): number[] {
    const all = [1, 2, 3, 4, 5, 6, 7, 8];
    if (!this.routeQuestionId) return all;
    return [this.routeQuestionId, ...all.filter((x) => x !== this.routeQuestionId)];
  }

  /** כשמשתמש פותח/סוגר פאנל – מעדכן expandedQuestionIndex */
  onPanelExpandedChange(expanded: boolean, index: number): void {
    this.expandedQuestionIndex = expanded ? index : null;
  }

  onSelectArrivalCluster(cluster: any) {
    const isSame = this.selectedArrivalClusterSerialName === cluster?.serial_name;
    this.selectedArrivalClusterSerialName = isSame ? null : cluster?.serial_name ?? null;
  }

  onSelectPaperTypesCluster(cluster: any) {
    const isSame = this.selectedPaperTypesClusterSerialName === cluster?.serial_name;
    this.selectedPaperTypesClusterSerialName = isSame ? null : cluster?.serial_name ?? null;
  }

  togglePaperExplain(key: string): void {
    this.expandedPaperExplainKey = this.expandedPaperExplainKey === key ? null : key;
  }

  onPaperExplainClick(printer: any, paper: any): void {
    const paperKey = this.getPaperTypeNameKey(paper);
    const fullKey = printer?.serialName + '_' + paperKey;
    this.togglePaperExplain(fullKey);
  }

  /** מיפוי שם עברי למפתח תרגום – כמו ב־edit-instructions */
  private static readonly PAPER_NAME_TO_KEY: Record<string, string> = {
    'פשוט': 'PLAIN',
    'מצופה': 'COATED',
    'פוטו': 'PHOTO',
  };

  /** מפתח תרגום לשם הנייר – paper-type.name.X */
  getPaperTypeNameKey(paper: any): string {
    if (!paper || !paper.name) return '';
    const name = String(paper.name).trim();
    const key = QAndAComponent.PAPER_NAME_TO_KEY[name] || name;
    return key;
  }

  /** מפתח תרגום להסבר הנייר – paper-type.description.X */
  getPaperDescriptionKey(paper: any): string {
    const key = this.getPaperTypeNameKey(paper);
    return key ? 'paper-type.description.' + key : '';
  }

  /** האם להציג הסבר נייר */
  isPaperExplainExpanded(printerSerialName: string, paperTypeKey: string): boolean {
    const fullKey = printerSerialName + '_' + paperTypeKey;
    return this.expandedPaperExplainKey === fullKey;
  }

  /** טקסט רוחב לנייר פלוטר – "רוחב: X ס"מ" */
  getPaperWidthText(paper: any, printType: 'plotter' | 'express' | 'pics'): string {
    if (printType !== 'plotter' || !paper?.paperWidth) return '';
    const width = (Number(paper.paperWidth) / 1000).toFixed(1);
    return this.translate.instant('q-and-a.5.paper-width', { width });
  }

  /** טקסט מידות לנייר אקספרס/תמונות – "מידות: X × Y ס"מ" */
  getPaperDimensionsText(paper: any, printType: 'plotter' | 'express' | 'pics'): string {
    if (printType === 'plotter' || !paper) return '';
    if (paper.paperWidth != null && paper.paperHeight != null) {
      const width = (Number(paper.paperWidth) / 1000).toFixed(1);
      const height = (Number(paper.paperHeight) / 1000).toFixed(1);
      return this.translate.instant('q-and-a.5.paper-dimensions', { width, height });
    }
    if (printType === 'pics' && paper.name) {
      const dims = QAndAComponent.PICS_DIMENSIONS_CM[paper.name];
      if (dims) {
        return this.translate.instant('q-and-a.5.paper-dimensions', {
          width: dims.width.toFixed(2),
          height: dims.height.toFixed(2),
        });
      }
    }
    return '';
  }

  private static readonly PICS_DIMENSIONS_CM: Record<string, { width: number; height: number }> = {
    SM: { width: 15.24, height: 10.16 },
    MD: { width: 17.78, height: 12.7 },
    LG: { width: 20.32, height: 15.24 },
  };

  /** מדפסות בקלאסטר הנבחר לשאלת סוגי נייר – כולל paperTypes מכל branch */
  get selectedPaperTypesClusterPrinters(): { serialName: string; displayName: string; printType: 'plotter' | 'express' | 'pics'; paperTypes: any[] }[] {
    if (!this.selectedPaperTypesClusterSerialName) return [];
    const cluster = this.clusters.find((c) => c.serial_name === this.selectedPaperTypesClusterSerialName);
    if (!cluster) return [];
    const result: { serialName: string; displayName: string; printType: 'plotter' | 'express' | 'pics'; paperTypes: any[] }[] = [];
    const add = (branch: any, type: 'plotter' | 'express') => {
      if (!branch?.serial_name) return;
      const sn = branch.serial_name;
      const printType: 'plotter' | 'express' | 'pics' = type === 'plotter' ? 'plotter' : branch?.isPicsPrinter ? 'pics' : 'express';
      const paperTypes = branch?.paperTypes && Array.isArray(branch.paperTypes) ? branch.paperTypes : [];
      result.push({
        serialName: sn,
        displayName: this.getPrinterDisplayName(sn, type) || sn,
        printType,
        paperTypes,
      });
    };
    (cluster.plotter || []).forEach((b: any) => add(b, 'plotter'));
    (cluster.express || []).forEach((b: any) => add(b, 'express'));
    return result;
  }

  /** מדפסות בקלאסטר הנבחר – לשימוש ב-ngFor */
  get selectedArrivalClusterPrinters(): { serialName: string; displayName: string; addressKey: string }[] {
    if (!this.selectedArrivalClusterSerialName) return [];
    const cluster = this.clusters.find((c) => c.serial_name === this.selectedArrivalClusterSerialName);
    return this.getPrintersInCluster(cluster);
  }

  // ===============
}
